import { useMemo, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { InkDocumentV1, InkPointerType, InkStroke } from '../../types/ink';

export type InkSemanticEvent =
  | { type: 'stroke_started'; pointerType: InkPointerType }
  | { type: 'stroke_committed'; pointerType: InkPointerType; pointCount: number }
  | { type: 'erase_used' }
  | { type: 'undo_used' };

type Props = {
  document: InkDocumentV1;
  backgroundDocument?: InkDocumentV1;
  onChange: (document: InkDocumentV1) => void;
  onSemanticEvent?: (event: InkSemanticEvent) => void;
  resolveProblemId?: (x: number, y: number) => string | undefined;
  onProblemFirstInteraction?: (problemId: string) => void;
  canStartStroke?: (problemId: string | undefined) => boolean;
};

function pointerType(value: string): InkPointerType {
  return value === 'pen' || value === 'touch' || value === 'mouse' ? value : 'unknown';
}

export function WorksheetInkCanvas({ document, backgroundDocument, onChange, onSemanticEvent, resolveProblemId, onProblemFirstInteraction, canStartStroke }: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const activeStroke = useRef<InkStroke | null>(null);
  const activeProblemId = useRef<string | null>(null);
  const documentRef = useRef(document);
  documentRef.current = document;

  const backgroundPaths = useMemo(() => (backgroundDocument?.strokes ?? []).map((stroke) => ({
    id: `background-${stroke.id}`,
    d: stroke.points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' '),
  })), [backgroundDocument?.strokes]);

  const paths = useMemo(() => document.strokes.map((stroke) => ({
    id: stroke.id,
    d: stroke.points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' '),
  })), [document.strokes]);

  function pointFromEvent(event: ReactPointerEvent<SVGSVGElement>) {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: ((event.clientX - rect.left) / rect.width) * document.width,
      y: ((event.clientY - rect.top) / rect.height) * document.height,
      t: Math.round(performance.now()),
      pressure: Number.isFinite(event.pressure) ? event.pressure : undefined,
    };
  }

  function workRegionProblemId(clientX: number, clientY: number) {
    const svg = svgRef.current;
    const paper = svg?.closest<HTMLElement>('.v2-paper');
    if (!svg || !paper) return undefined;
    const blocks = Array.from(paper.querySelectorAll<HTMLElement>('[data-problem-id]'));
    for (const block of blocks) {
      const region = block.querySelector<HTMLElement>('[data-ink-region="work"]');
      if (!region) continue;
      const rect = region.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
        return block.dataset.problemId;
      }
    }
    return undefined;
  }

  function onPointerDown(event: ReactPointerEvent<SVGSVGElement>) {
    const point = pointFromEvent(event);
    if (!point) return;
    const type = pointerType(event.pointerType);
    // Ink is intentionally constrained to the visible worksheet work box.
    // The authoritative problem placement still gates correction ownership,
    // while the DOM work-region gate prevents strokes crossing prompts/answers.
    const problemId = workRegionProblemId(event.clientX, event.clientY) ?? resolveProblemId?.(point.x, point.y);
    if (!problemId || workRegionProblemId(event.clientX, event.clientY) !== problemId) return;
    if (canStartStroke && !canStartStroke(problemId)) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    activeProblemId.current = problemId;
    onProblemFirstInteraction?.(problemId);
    const stroke: InkStroke = { id: `${Date.now()}-${event.pointerId}`, pointerType: type, points: [point] };
    activeStroke.current = stroke;
    documentRef.current = { ...documentRef.current, strokes: [...documentRef.current.strokes, stroke] };
    onChange(documentRef.current);
    onSemanticEvent?.({ type: 'stroke_started', pointerType: type });
  }

  function onPointerMove(event: ReactPointerEvent<SVGSVGElement>) {
    if (!activeStroke.current || event.buttons === 0) return;
    const point = pointFromEvent(event);
    if (!point) return;
    const problemId = workRegionProblemId(event.clientX, event.clientY);
    if (!problemId || problemId !== activeProblemId.current) {
      finishStroke();
      return;
    }
    const previous = activeStroke.current.points.at(-1);
    if (previous && Math.hypot(point.x - previous.x, point.y - previous.y) < 2.2) return;
    activeStroke.current = { ...activeStroke.current, points: [...activeStroke.current.points, point] };
    documentRef.current = {
      ...documentRef.current,
      strokes: documentRef.current.strokes.map((stroke) => stroke.id === activeStroke.current?.id ? activeStroke.current : stroke),
    };
    onChange(documentRef.current);
  }

  function finishStroke() {
    if (!activeStroke.current) {
      activeProblemId.current = null;
      return;
    }
    onSemanticEvent?.({
      type: 'stroke_committed',
      pointerType: activeStroke.current.pointerType,
      pointCount: activeStroke.current.points.length,
    });
    activeStroke.current = null;
    activeProblemId.current = null;
  }

  function undo() {
    documentRef.current = { ...documentRef.current, strokes: documentRef.current.strokes.slice(0, -1) };
    onChange(documentRef.current);
    onSemanticEvent?.({ type: 'undo_used' });
  }

  return (
    <div className="v2-ink-shell">
      <button type="button" className="btn btn--small no-print" onClick={undo} disabled={document.strokes.length === 0}>Undo</button>
      <svg
        ref={svgRef}
        className="v2-ink-layer"
        viewBox={`0 0 ${document.width} ${document.height}`}
        preserveAspectRatio="none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishStroke}
        onPointerCancel={finishStroke}
      >
        {backgroundPaths.map((path) => <path key={path.id} d={path.d} className="v2-ink-stroke v2-ink-stroke--background" vectorEffect="non-scaling-stroke" />)}
        {paths.map((path) => <path key={path.id} d={path.d} className="v2-ink-stroke" vectorEffect="non-scaling-stroke" />)}
      </svg>
    </div>
  );
}
